<template>
  <van-popup :show="show" position="bottom" :style="{ height: '100%', width: '100%' }" @update:show="updateShow">
    <div v-if="optionsLoading" class="popup-loading">
      <van-loading type="spinner" />
      <span>加载中...</span>
    </div>
    <div v-else class="popup-content">
      <van-form @submit="handleSubmit">
        <van-field v-model="localForm.studentName" label="学员姓名" placeholder="请输入学员姓名" required />
        
        <DictSelect
          v-model="localForm.sex"
          name="sex"
          label="性别"
          placeholder="请选择性别"
          :columns="sys_user_sex"
          required
          @confirm="(data) => { localForm.sexText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.status"
          name="status"
          label="状态"
          placeholder="请选择状态"
          :columns="student_status"
          required
          @confirm="(data) => { localForm.statusText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.grade"
          name="grade"
          label="年级"
          placeholder="请选择年级"
          :columns="grade_list"
          required
          @confirm="(data) => { localForm.gradeText = data.label }"
        />
        
        <DictSelect
          v-model="localForm.schoolId"
          name="school"
          label="所属校区"
          placeholder="请选择所属校区"
          :columns="schoolList"
          :column-names="schoolFieldNames"
          required
          @confirm="(data) => { localForm.schoolText = data.label; localForm.schoolId = data.value }"
        />
        
        <van-field v-model="localForm.plannerText" label="规划师" placeholder="请选择规划师" readonly clickable required @click="showPlannerPicker = true" />
        <van-field v-model="localForm.phone" label="手机号" placeholder="请输入手机号" type="tel" required />
        <van-field v-model="localForm.birthday" label="出生日期" placeholder="请选择出生日期" readonly clickable required @click="openBirthdayPicker" />
        
        <DictSelect
          v-model="localForm.source"
          name="source"
          label="学生来源"
          placeholder="请选择学生来源"
          :columns="student_source"
          required
          @confirm="(data) => { localForm.sourceText = data.label }"
        />

        <div class="form-actions">
          <van-button type="default" block native-type="button" @click="closePopup">取消</van-button>
          <van-button type="primary" block native-type="submit" :loading="submitLoading">确定</van-button>
        </div>
      </van-form>
    </div>

    <van-popup :show="showBirthdayPicker" position="bottom" @update:show="showBirthdayPicker = $event">
      <van-date-picker v-model="selectedBirthday" type="date" @confirm="onBirthdayConfirm" @cancel="showBirthdayPicker = false" />
    </van-popup>
    <van-popup :show="showPlannerPicker" position="bottom" @update:show="showPlannerPicker = $event">
      <div class="user-picker-popup">
        <van-search v-model="plannerSearchQuery" placeholder="搜索规划师姓名" show-action @cancel="showPlannerPicker = false" />
        <van-cell v-for="planner in filteredPlannerList" :key="planner.userId" :title="planner.nickName" @click="selectPlanner(planner)" />
      </div>
    </van-popup>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { showToast } from 'vant'
import { getSchoolDicts } from '@/api/dict'
import { getUserListByRoleKey } from '@/api/user'
import { useDict } from '@/utils/dict'
import { getCurrentDateArray, getInitialStudentForm } from '../formModels'
import { buildStudentPayload, getStudentFormError } from '../formSubmitters'
import DictSelect from '@/components/DictSelect.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  submitLoading: {
    type: Boolean,
    default: false
  },
  initialName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:show', 'submit'])

const { sys_user_sex, grade_list, student_status, student_source, loadDicts } = useDict(
  'sys_user_sex',
  'grade_list',
  'student_status',
  'student_source'
)

const localForm = ref(getInitialStudentForm())
const schoolList = ref([])
const plannerList = ref([])
const schoolFieldNames = { text: 'schoolName', value: 'schoolId' }
const optionsLoaded = ref(false)
const optionsPromise = ref(null)
const optionsLoading = ref(false)
const showBirthdayPicker = ref(false)
const showPlannerPicker = ref(false)
const plannerSearchQuery = ref('')
const selectedBirthday = ref(getCurrentDateArray())

const filteredPlannerList = computed(() => {
  const query = plannerSearchQuery.value.trim()
  if (!query) {
    return plannerList.value
  }
  return plannerList.value.filter(item => item.nickName.includes(query))
})

function cloneForm(form) {
  return { ...form }
}

async function ensureOptionsLoaded() {
  if (optionsLoaded.value) {
    return
  }
  if (optionsPromise.value) {
    await optionsPromise.value
    return
  }
  optionsLoading.value = true
  try {
    optionsPromise.value = Promise.all([
      loadDicts(),
      getSchoolDicts(),
      getUserListByRoleKey('planner')
    ])
    const [, schools, planners] = await optionsPromise.value
    schoolList.value = schools
    plannerList.value = planners
    optionsLoaded.value = true
  } finally {
    optionsPromise.value = null
    optionsLoading.value = false
  }
}

function buildInitialForm() {
  const form = getInitialStudentForm()
  form.studentName = props.initialName || ''
  form.status = 1
  form.statusText = student_status.value.find(item => Number(item.dictValue) === 1)?.dictLabel || ''
  form.birthday = getCurrentDateArray().join('-')
  if (schoolList.value.length === 1) {
    form.schoolId = String(schoolList.value[0].schoolId)
    form.schoolText = schoolList.value[0].schoolName
  }
  return form
}

async function initializePopup() {
  await ensureOptionsLoaded()
  localForm.value = buildInitialForm()
  selectedBirthday.value = localForm.value.birthday.split('-')
  plannerSearchQuery.value = ''
}

function updateShow(value) {
  emit('update:show', value)
}

function closePopup() {
  updateShow(false)
}

function handleSubmit() {
  const form = cloneForm(localForm.value)
  const error = getStudentFormError(form)
  if (error) {
    showToast(error)
    return
  }
  emit('submit', buildStudentPayload(form))
}

function openBirthdayPicker() {
  selectedBirthday.value = localForm.value.birthday ? localForm.value.birthday.split('-') : getCurrentDateArray()
  showBirthdayPicker.value = true
}

function onBirthdayConfirm() {
  localForm.value.birthday = `${selectedBirthday.value[0]}-${selectedBirthday.value[1]}-${selectedBirthday.value[2]}`
  showBirthdayPicker.value = false
}

function selectPlanner(planner) {
  localForm.value.plannerId = String(planner.userId)
  localForm.value.plannerText = planner.nickName
  showPlannerPicker.value = false
}

watch(() => props.show, value => {
  if (value) {
    initializePopup().catch(error => {
      showToast(error?.message || '加载学员表单失败')
      updateShow(false)
    })
  }
})
</script>

<style scoped>
.popup-content {
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
}

.popup-loading {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #666;
}

.form-actions {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
}

.form-actions .van-button {
  flex: 1;
  height: 44px;
  border-radius: 8px;
}

.user-picker-popup {
  height: 80vh;
  overflow-y: auto;
}
</style>
